<template>
    <ui-dropdown placement="bottom-start" menu-class="w-96">
        <template #default="dropdownScope">
            <slot v-bind="scope(dropdownScope)"></slot>
        </template>

        <template #menu="{ close }">
            <div :class="(creatingNewSupplier || confirmingSupplier) ? 'p-5 pt-3' : 'p-3 max-h-64 overflow-y-auto'">

                <!-- New supplier form. -->
                <div v-if="creatingNewSupplier">
                    <div class="text-xs font-medium text-gray-400 mb-4">Create new supplier</div>
                    Not currently used...
                </div>

                <!-- Confirming supplier. -->
                <div v-else-if="confirmingSupplier">
                    <div class="text-xs font-medium text-gray-400 mb-4">Use supplier "{{ confirmingSupplier.company_name }}"</div>
                    <p class="mt-4 text-sm">
                        Updating the supplier will update all instances of this material in this estimate.
                        It will affect <strong>{{ materialSpec.spec_usages.length }} specs</strong>
                        used in <strong>{{ materialSpec.item_usages.length }} different items</strong>.
                    </p>
                    <p class="mt-4 text-sm">
                        The new price for the material will be <strong>{{ confirmingSupplier.cost | toCurrency }}</strong><span v-if="confirmingSupplier.is_from_mpa"> which is coming <strong>from a price agreement</strong></span>.
                    </p>
                    <p class="mt-4 text-sm">Are you sure you want to continue?</p>
                    <div class="mt-6 flex justify-end space-x-6">
                        <ui-button-new @click.stop.prevent="confirmingSupplier = null">Cancel</ui-button-new>
                        <ui-button-new warning @click.stop.prevent="selectSupplier(confirmingSupplier, close)">
                            Yes, update the supplier
                        </ui-button-new>
                    </div>
                </div>

                <div v-else class="space-y-4">

                    <!-- Detach current supplier. -->

                    <div v-if="hasSupplier">
                        <div class="ml-2 text-xs font-medium text-gray-400 mb-1">Current Supplier</div>
                        <p class="ml-2 mb-1" v-text="materialSpec.company.name"></p>
                        <button v-if="hasSupplier" class="flex items-center text-gray-500" :class="buttonClass" @click.stop.prevent="detachSupplier(close)">
                            <i class="fas fa-fw mr-2 text-sm flex-shrink-0 fa-unlink text-gray-400"></i>
                            {{ detachSupplierText }}
                        </button>
                    </div>

                    <!-- Suppliers attached to the material. -->
                    <div>
                        <div class="ml-2 text-xs font-medium text-gray-400 mb-1">Select a supplier attached to the material</div>
                        <div v-if="loading" class="ml-2 flex items-center space-x-2 text-gray-500 text-sm">
                            <svg class="animate-spin w-5 h-5 text-gray-300" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            <div>Loading...</div>
                        </div>
                        <div v-else-if="attachedSuppliers.length === 0" class="ml-2 text-gray-500 text-sm">
                            <div>No attached supplier found.</div>
                        </div>
                        <div v-else class="space-y-1">
                            <button
                                v-for="supplier in attachedSuppliers"
                                :key="`supplier-attached-${supplier.company_id}`"
                                class="flex items-center"
                                :class="buttonClass"
                                :title="supplier.company_name"
                                @click.stop.prevent="confirmSupplier(supplier, close)"
                            >
                                <i
                                    class="fas fa-fw mr-2 text-sm flex-shrink-0 fa-link text-green-500"
                                ></i>
                                <div class="flex flex-1 flex-col truncate">
                                    <div class="truncate w-full">{{ supplier.company_name }}</div>
                                    <div class="truncate w-full text-xs text-gray-500">
                                        {{ supplier.cost | toCurrency }}
                                        {{ supplier.is_from_mpa ? '(MPA)' : '' }}
                                    </div>
                                </div>
                            </button>
                        </div>
                    </div>

                    <!-- Other suppliers. -->
                    <div v-if="query">
                        <div class="ml-2 text-xs font-medium text-gray-400 mb-1">Select another supplier</div>
                        <div v-if="loading" class="ml-2 flex items-center space-x-2 text-gray-500 text-sm">
                            <svg class="animate-spin w-5 h-5 text-gray-300" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            <div>Loading...</div>
                        </div>
                        <div v-else-if="otherSuppliers.length === 0" class="ml-2 text-gray-500 text-sm">
                            <div>No other supplier found.</div>
                        </div>
                        <div v-else class="space-y-1">
                            <button
                                v-for="supplier in otherSuppliers"
                                :key="`supplier-other-${supplier.company_id}`"
                                class="flex items-center"
                                :class="buttonClass"
                                :title="supplier.company_name"
                                @click.stop.prevent="confirmSupplier(supplier, close)"
                            >
                                <i
                                    class="fas fa-fw mr-2 text-sm flex-shrink-0 fa-unlink text-gray-400"
                                ></i>
                                <div class="flex flex-1 flex-col truncate">
                                    <div class="truncate w-full">{{ supplier.company_name }}</div>
                                </div>
                            </button>
                        </div>
                    </div>

                    <!-- Empty State. -->
                    <div v-else class="ml-2 text-gray-500 text-sm">
                        Or start typing to search for suppliers...
                    </div>
                </div>
            </div>
        </template>
    </ui-dropdown>
</template>

<script>
import { debounce } from 'lodash'
import { Material } from '@models'

export default {
    props: {
        materialSpec: Object,
        estimate: Object,
        buttonClass: {
            type: String,
            default: 'text-left w-full rounded-md px-2 py-1 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-600',
        },
    },
    data () {
        return {
            query: '',
            loading: false,
            suppliers: [],
            creatingNewSupplier: false,
            confirmingSupplier: null,
        }
    },
    computed: {
        hasSupplier () {
            return !! this.materialSpec.company_id
        },
        detachSupplierText () {
            return this.estimate ? 'Remove supplier' : 'Use default supplier'
        },
        attachedSuppliers () {
            if (! this.query) {
                return this.materialSpec.attached_companies ?? []
            }

            return this.suppliers.filter(supplier => supplier.is_attached)
        },
        otherSuppliers () {
            return this.suppliers.filter(supplier => ! supplier.is_attached)
        },
    },
    methods: {
        scope (dropdownScope) {
            return {
                query: this.query,
                setQuery: this.setQuery,
                ...dropdownScope,
            }
        },
        setQuery (query) {
            this.query = query
            if (! this.query) return
            this.loading = true
            this.fetchSuppliers()
        },
        confirmSupplier (supplier, close) {
            if (! this.estimate) {
                return this.selectSupplier(supplier, close)
            }
            this.confirmingSupplier = supplier
        },
        selectSupplier (supplier, close) {
            this.$emit('selected', supplier)
            this.confirmingSupplier = null
            this.query = ''
            close()
        },
        detachSupplier (close) {
            this.selectSupplier(null, close)
        },
        fetchSuppliers: debounce(async function () {
            try {
                this.loading = true
                const material = new Material(this.materialSpec.material)
                this.suppliers = await material.searchCompanies(this.query, this.estimate?.id)
            } finally {
                this.loading = false
            }
        }, 300),
    },
}
</script>
